/**
 * SCAFFOLD — temporary fake referral data
 * =========================================
 *
 * The referrals backend is not yet wired end-to-end. While the indexer / DB /
 * service work catches up, the portfolio referrals panel renders this fake
 * dataset so the UI can be reviewed in dev. Production builds also surface
 * these fakes (since the constant is on) — that is INTENTIONAL until the
 * backend ships, after which:
 *
 *   1. Flip `USE_FAKE_REFERRAL_DATA` to `false` (or delete every consumer's
 *      branch that reads it).
 *   2. Delete this file.
 *   3. The three consumers — `<ReferralsPanel>`, `<EarningsSparkline>`,
 *      `<ShowRefereesSheet>` — fall back to live SDK data automatically.
 *
 * Removal grep:
 *   grep -rln '_fake-data' frontend/src
 *   grep -rln 'USE_FAKE_REFERRAL_DATA' frontend/src
 *
 * NO consumer outside `frontend/src/components/portfolio/referrals/` may
 * import this file — it is internal scaffolding, not a stable surface.
 */

import type {
  EarningsChartBucket,
  Referee,
  ReferralSummary,
} from '@/components/portfolio/referrals/types';

/** Master flag — flip to `false` (or delete this module) when the backend ships. */
export const USE_FAKE_REFERRAL_DATA: boolean = true;

const FAKE_USER_ID = 'fake-viewer-uuid';

/** Matches the Figma mockup (node 3396:209): hero shows the $252.21 currently-
 *  claimable balance; the "Total earned" stat cell shows the $124.2k lifetime
 *  earnings; "Referees" stat cell shows 4 (3 L1 + 1 L2). */
export const FAKE_REFERRAL_SUMMARY: ReferralSummary = {
  userId: FAKE_USER_ID,
  l1Count: 3,
  l2Count: 1,
  totalL1Earnings: '93150000000', // $93,150 — lifetime
  totalL2Earnings: '31050000000', // $31,050 — lifetime
  combinedEarnings: '124200000000', // $124,200 ($124.2k lifetime)
  availableToClaim: '252210000', // $252.21 currently claimable
};

/** 30 days of cumulative earnings buckets — monotonically growing curve so
 *  the area sparkline tells a clear "growth" story across every range. */
export const FAKE_EARNINGS_BUCKETS_30D: ReadonlyArray<EarningsChartBucket> = (() => {
  const days = 30;
  const start = new Date();
  start.setUTCHours(0, 0, 0, 0);
  start.setUTCDate(start.getUTCDate() - (days - 1));
  // Sigmoidal-ish growth from 0 → ~252.21 USD over the window so the curve
  // has a visible inflection rather than a flat ramp.
  function cumulative(i: number): number {
    const t = i / (days - 1); // 0 → 1
    const eased = 1 / (1 + Math.exp(-8 * (t - 0.55))); // sigmoid centred at 55%
    return eased * 252_210_000;
  }
  const buckets: EarningsChartBucket[] = [];
  for (let i = 0; i < days; i++) {
    const ts = new Date(start.getTime() + i * 24 * 60 * 60 * 1000).toISOString();
    const total = Math.round(cumulative(i));
    const l1 = Math.round(total * 0.747); // ~74.7% from direct
    const l2 = total - l1;
    buckets.push({
      bucket: ts,
      l1Earnings: String(l1),
      l2Earnings: String(l2),
      totalEarnings: String(total),
    });
  }
  return buckets;
})();

/** Four referees — three L1 (direct) plus one L2 (indirect). The compact
 *  row rendering doesn't distinguish tiers; the count split shows up only in
 *  the panel's stat cells. Names + avatars varied so the list looks alive.
 *  Extra rows kept (8 total) so the dialog still demonstrates scrollable
 *  history beyond the 4-referee count. */
export const FAKE_REFEREES: ReadonlyArray<Referee> = [
  {
    refereeUserId: 'fake-r-001',
    displayName: 'Kate the opinionated',
    handle: 'kateopinionated',
    avatarUrl: null,
    avatarNsfw: false,
    // ALLOW-RASTER-LITERAL: dev-only fake-data scaffold (see file header).
    avatarPreferences: { rasterise: false },
    linkedAt: '2026-05-08T10:30:00Z',
    hasDownstream: true,
    totalTrades: 24,
    totalVolume: '482300000', // $482.30
    earningsGenerated: '94120000', // $94.12
  },
  {
    refereeUserId: 'fake-r-002',
    displayName: 'Marcus Chen',
    handle: 'marcus',
    avatarUrl: null,
    avatarNsfw: false,
    // ALLOW-RASTER-LITERAL: dev-only fake-data scaffold (see file header).
    avatarPreferences: { rasterise: false },
    linkedAt: '2026-04-29T14:12:00Z',
    hasDownstream: false,
    totalTrades: 11,
    totalVolume: '212500000', // $212.50
    earningsGenerated: '41320000', // $41.32
  },
  {
    refereeUserId: 'fake-r-003',
    displayName: 'Aiko Tanaka',
    handle: 'aiko',
    avatarUrl: null,
    avatarNsfw: false,
    // ALLOW-RASTER-LITERAL: dev-only fake-data scaffold (see file header).
    avatarPreferences: { rasterise: false },
    linkedAt: '2026-04-21T09:05:00Z',
    hasDownstream: true,
    totalTrades: 8,
    totalVolume: '154700000', // $154.70
    earningsGenerated: '28910000', // $28.91
  },
  {
    refereeUserId: 'fake-r-004',
    displayName: 'Diego Ruiz',
    handle: 'diego',
    avatarUrl: null,
    avatarNsfw: false,
    // ALLOW-RASTER-LITERAL: dev-only fake-data scaffold (see file header).
    avatarPreferences: { rasterise: false },
    linkedAt: '2026-04-12T16:45:00Z',
    hasDownstream: false,
    totalTrades: 5,
    totalVolume: '88200000', // $88.20
    earningsGenerated: '14310000', // $14.31
  },
  {
    refereeUserId: 'fake-r-005',
    displayName: 'Priya Sharma',
    handle: 'priya',
    avatarUrl: null,
    avatarNsfw: false,
    // ALLOW-RASTER-LITERAL: dev-only fake-data scaffold (see file header).
    avatarPreferences: { rasterise: false },
    linkedAt: '2026-04-04T11:20:00Z',
    hasDownstream: false,
    totalTrades: 3,
    totalVolume: '41600000', // $41.60
    earningsGenerated: '9780000', // $9.78
  },
  {
    refereeUserId: 'fake-r-006',
    displayName: 'Lukas Becker',
    handle: 'lukas',
    avatarUrl: null,
    avatarNsfw: false,
    // ALLOW-RASTER-LITERAL: dev-only fake-data scaffold (see file header).
    avatarPreferences: { rasterise: false },
    linkedAt: '2026-03-28T08:00:00Z',
    hasDownstream: false,
    totalTrades: 6,
    totalVolume: '95400000', // $95.40
    earningsGenerated: '23510000', // $23.51
  },
  {
    refereeUserId: 'fake-r-007',
    displayName: 'Sofia Andersson',
    handle: 'sofia',
    avatarUrl: null,
    avatarNsfw: false,
    // ALLOW-RASTER-LITERAL: dev-only fake-data scaffold (see file header).
    avatarPreferences: { rasterise: false },
    linkedAt: '2026-03-19T13:32:00Z',
    hasDownstream: false,
    totalTrades: 4,
    totalVolume: '63100000', // $63.10
    earningsGenerated: '15880000', // $15.88
  },
  {
    refereeUserId: 'fake-r-008',
    displayName: 'Theo Williams',
    handle: 'theo',
    avatarUrl: null,
    avatarNsfw: false,
    // ALLOW-RASTER-LITERAL: dev-only fake-data scaffold (see file header).
    avatarPreferences: { rasterise: false },
    linkedAt: '2026-03-09T18:50:00Z',
    hasDownstream: false,
    totalTrades: 2,
    totalVolume: '38900000', // $38.90
    earningsGenerated: '24370000', // $24.37
  },
];
